import React, { useEffect, useMemo } from 'react';
import { View, Text, ImageBackground, ScrollView, SafeAreaView, ViewStyle, BackHandler } from 'react-native';

import { t } from 'Locale';
// eslint-disable-next-line import/no-unresolved
import { Button, Hr, Icon, ModalHeader } from 'LocalGluons';
import { getDayDotMonthDotYear } from 'Utils/dateUtils';
import { PLATFORMS } from 'Options';
import defaultStyles from './PackageInfoScene.jss';
import getVoyoPackageTexts, { SUBSCRPTION_TYPE } from './getVoyoPackageTexts';
import { usePlayouts } from './usePlayouts';
import getCountdouwnDaysHoursMinutes from './getCountdouwnDaysHoursMinutes';

interface PackageInfoSceneProps {
    platform: string;
    close?: () => void | undefined;
    onContinue?: () => void;
    hr?: boolean;
    backgroundImage?: string;
    containerStyle?: ViewStyle;
    buttonStyle?: ViewStyle;
    payload?: {
        title: string,
        description: string,
        backButtonText: string,
        continueButtonText: string,
        infoDescription: string
    }
}

const PackageInfoScene = ({
    close,
    platform,
    backgroundImage,
    onContinue,
    containerStyle,
    buttonStyle,
    hr = true,
    payload
}: PackageInfoSceneProps): React.ReactElement => {
    const [myRenawalDate, myPlatformSubscription] = usePlayouts(platform);

    const { title, description, backButtonText, continueButtonText, infoDescription } = useMemo(
        () => payload || getVoyoPackageTexts(myPlatformSubscription?.type as SUBSCRPTION_TYPE),
        [myPlatformSubscription]
    );

    const { expirationDaysCountdown, expirationHoursCountdown, expirationMinutesCountdown } =
        getCountdouwnDaysHoursMinutes(myRenawalDate);

    useEffect(() => {
        const backAction = () => {
            close();
            return false;
        };
        const subscription = BackHandler.addEventListener('hardwareBackPress', backAction);
        return () => {
            subscription.remove();
        };

    }, []);

    return (
        <SafeAreaView style={defaultStyles.safeAreaContainer}>
            <ScrollView contentContainerStyle={defaultStyles.scrollContainer}>
                <ImageBackground
                    source={{ uri: backgroundImage }}
                    style={defaultStyles.imageBackground}
                    resizeMode='cover'
                >
                    <View style={[defaultStyles.container, containerStyle]}>
                        <ModalHeader
                            style={{
                                iconContainer: defaultStyles.iconCloseContainer
                            }}
                            onPress={close}
                        />
                        <View style={defaultStyles.logoBox}>
                            {platform === PLATFORMS.VOYO && (
                                <Icon style={defaultStyles.logo} name="voyo_logo" />
                            )}
                        </View>

                        <Text style={defaultStyles.title}>{t(title)}</Text>

                        {hr && <Hr style={defaultStyles.hrStyle} />}

                        {myPlatformSubscription?.type === SUBSCRPTION_TYPE.TRIAL && (
                            <>
                                <Text style={defaultStyles.subtitle}>
                                    {t(description)
                                        .split('{0}')
                                        .join(expirationDaysCountdown.toString())
                                        .split('{1}')
                                        .join(expirationHoursCountdown.toString())
                                        .split('{2}')
                                        .join(expirationMinutesCountdown.toString())}
                                </Text>

                                <Text style={defaultStyles.subtitleInfo}>{`\n${t(
                                    infoDescription
                                )}`}</Text>
                                {/* <View style={defaultStyles.buttonContainer}> */}
                                <Button
                                    onPress={onContinue}
                                    style={{
                                        button: {
                                            ...defaultStyles.buttonContinue,
                                            ...buttonStyle
                                        }
                                    }}
                                    title={t(continueButtonText)}
                                />

                                <Button
                                    onPress={close}
                                    style={{
                                        button: {
                                            ...defaultStyles.button,
                                            ...buttonStyle
                                        }
                                    }}
                                    title={t(backButtonText)}
                                    isSecondary
                                />
                                {/* </View> */}
                            </>
                        )}
                        {myPlatformSubscription?.type === SUBSCRPTION_TYPE.TASTING && (
                            <>
                                <Text style={defaultStyles.subtitle}>
                                    {t(description)
                                        .replace(
                                            '{0}',
                                            `${myPlatformSubscription.playoutsActualCount}/${myPlatformSubscription.playoutsMaxCount}`
                                        )
                                        .replace(
                                            '{1}',
                                            `${myRenawalDate !== null &&
                                            getDayDotMonthDotYear(new Date(myRenawalDate))
                                            }\n`
                                        )}
                                </Text>
                                {/* <View style={defaultStyles.buttonContainer}> */}
                                <Button
                                    onPress={onContinue}
                                    style={{
                                        button: {
                                            ...defaultStyles.button,
                                            ...defaultStyles.buttonContinue,
                                            ...buttonStyle
                                        }
                                    }}
                                    title={t(continueButtonText)}
                                />
                                <Button
                                    onPress={close}
                                    style={{ button: { ...defaultStyles.button, ...buttonStyle } }}
                                    title={t(backButtonText)}
                                    isSecondary
                                />
                                {/* </View> */}
                                <Text style={defaultStyles.subtitleInfoTasting}>
                                    {t(infoDescription)}
                                </Text>
                            </>
                        )}

                        {(myPlatformSubscription?.type === SUBSCRPTION_TYPE.FULL ||
                            platform === PLATFORMS.CANALPLUS) && (
                                <>
                                    <Text style={defaultStyles.subtitleFull}>
                                        {t(description)
                                            .split('{0}')
                                            .join(
                                                `${myRenawalDate !== null &&
                                                getDayDotMonthDotYear(new Date(myRenawalDate))
                                                }`
                                            )}
                                    </Text>

                                    {/* <View style={defaultStyles.buttonContainer}> */}
                                    <Button
                                        onPress={close}
                                        style={{
                                            button: {
                                                ...defaultStyles.button,
                                                ...defaultStyles.buttonContinue,
                                                ...buttonStyle
                                            }
                                        }}
                                        title={t(continueButtonText)}
                                    />
                                    {/* </View> */}
                                </>
                            )}
                    </View>
                </ImageBackground>
            </ScrollView>
        </SafeAreaView>
    );
};

export default PackageInfoScene;
